import {
  ToolbarItem,
  SideNavGroup,
  SideNavLink,
} from '@amzn/aac-chrome-internal-api';
import React from 'react';
import { useConsolidatedNavContext } from 'src/providers/ConsolidatedNavContextProvider';
import { Flex, Icon, Text } from '@amzn/storm-ui';
import {
  AllToolsTitleWrapper,
  AccordionLinkWrapper,
  AccordionContentGroupWrapper,
  AccordionContentInnerWrapper,
  AccordionContentSubTitle,
  StyledDivider as Divider,
} from './styles';
import { useSidenavBundle } from 'src/providers/PantherLocalizationProvider';
import { SvgIcon } from 'src/commons/components/SvgIcon/SvgIcon';
import { getTaktAttributes } from 'src/commons/utils/logWithTakt';
import { chromeComponents } from 'src/constants';
import { renderFlag } from 'src/commons/components/LaunchFlag/LaunchFlag.utils';
import { externalLink } from '@amzn/storm-ui-icons';
import { AccordionWrapper, AccordionLink } from '../styles';

interface AllToolsToolbarItemProps {
  toolbarItem: ToolbarItem;
}

const AllToolsToolbarItem: React.VFC<AllToolsToolbarItemProps> = ({
  toolbarItem,
}) => {
  const id = toolbarItem.id;
  const bundle = useSidenavBundle();

  const { isNavOpen, activeCategoryId } = useConsolidatedNavContext();

  if (!toolbarItem) {
    return null;
  }

  const isActive = id === activeCategoryId;
  const iconPath = toolbarItem.iconPath;
  const title = toolbarItem.toolTip;

  const ariaLabel = bundle
    .getMessage('sidenav-show-links-aria-label')
    .replace('{title}', title ?? '');

  return (
    <AccordionWrapper isActive={isActive}>
      <AllToolsTitleWrapper
        gap="xxsmall"
        isSelected={false}
        isNavOpen={isNavOpen}
        data-ccx-e2e-id={id}
        aria-label={ariaLabel}
        aria-expanded={true}
        {...getTaktAttributes(id, chromeComponents.ALL_TOOLS_NAV)}
      >
        <SvgIcon iconPath={iconPath} ariaLabel={title} isSelected={false} />
        <Text renderAs="span">{title}</Text>
      </AllToolsTitleWrapper>
      <AccordionContentInnerWrapper>
        {toolbarItem.sidebarGroups.map((group: SideNavGroup, index: number) => (
          <AccordionContentGroupWrapper
            key={`${index}-${group.title}`}
            direction="column"
            rowGap="xxxsmall"
          >
            {group.title && (
              <AccordionContentSubTitle>{group.title}</AccordionContentSubTitle>
            )}
            {group.links.map((sideNavLink: SideNavLink) => {
              const isExternalLink: boolean =
                sideNavLink.sidebarItemType === 'EXTERNAL_LINK';
              const { link, appName, calloutTag, id } = sideNavLink;
              return (
                <AccordionLinkWrapper
                  key={id}
                  isSelected={false}
                  data-ccx-e2e-id={`${id}-accordion-link-wrapper`}
                >
                  <AccordionLink
                    isSelected={false}
                    href={link.url}
                    target={isExternalLink ? '_blank' : '_self'}
                    data-ccx-e2e-id={id}
                    aria-label={link.label}
                    {...getTaktAttributes(id, chromeComponents.ALL_TOOLS_NAV, {
                      url: link.url,
                    })}
                  >
                    {link.label}
                  </AccordionLink>
                  <Flex
                    alignItems="center"
                    data-ccx-e2e-id={`sidenav-link-features-${appName}`}
                  >
                    {!!calloutTag && renderFlag(sideNavLink)}
                    {isExternalLink && (
                      <Icon type={externalLink} size="small" />
                    )}
                  </Flex>
                </AccordionLinkWrapper>
              );
            })}
            {index !== toolbarItem.sidebarGroups.length - 1 && <Divider />}
          </AccordionContentGroupWrapper>
        ))}
      </AccordionContentInnerWrapper>
    </AccordionWrapper>
  );
};

export default AllToolsToolbarItem;
